import { type ReactNode, useEffect } from 'react';

export interface BottomSheetProps {
  /** Sheet heading. Also used as the dialog's accessible name. */
  title: string;
  /** Optional sub-heading under the title. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Accessible label for the close button. Defaults to "Close". */
  closeLabel?: string;
  /** When false, backdrop/Escape dismissal and the close icon are disabled. Explicit actions may still call onClose. */
  dismissible?: boolean;
}

/**
 * BottomSheet — shared mobile bottom sheet: scrim + bottom-anchored panel with
 * a drag handle and a top-right close button (matching `Modal`'s convention).
 * Presentational only: forwards close events. Escape and scrim dismissal are enabled by default and can be disabled for protected forms.
 */
export function BottomSheet({ title, description, onClose, children, closeLabel = 'Close', dismissible = true }: BottomSheetProps) {
  useEffect(() => {
    if (!dismissible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dismissible, onClose]);

  return (
    <div
      role="presentation"
      onClick={dismissible ? onClose : undefined}
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/40 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className="relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[20px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-3)]"
      >
        <div className="shrink-0 border-b border-[var(--border)] px-5 pb-4 pt-3">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--surface-3)]" aria-hidden="true" />
          <h2 className="pr-10 text-[18px] font-bold text-[var(--text)]">{title}</h2>
          {description && <p className="mt-1 pr-10 text-[13px] text-[var(--text-3)]">{description}</p>}
        </div>
        {dismissible ? (
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-[9px] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        ) : null}
        <div className="min-h-0 overflow-y-auto px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4">{children}</div>
      </div>
    </div>
  );
}
